/**
 * Campaign short links: `suikamon.com/yt`, `/tt`, `/ig`, `/x`.
 *
 * A Short or a TikTok caption has no tappable link, so the link a viewer types from the screen
 * has to be short. Each one lands on the free starter page tagged with the channel it came from,
 * so a visit (and the hatch after it) says which channel earned it.
 *
 * The tag is also remembered IN MEMORY for the rest of the visit and attached to every analytics
 * event (`campaign_source`, …). Analytics only start once a visitor accepts them, usually a few
 * screens after landing, when the `utm_` parameters are long gone from the URL. Memory only: it
 * writes nothing to the device, so it needs no consent of its own, and a reload forgets it.
 */

/** Where every campaign short link lands: the free starter flow. */
export const CAMPAIGN_LANDING_PATH = "/play-free";

/** The campaign every short link belongs to (the 2026 Q4 gameplay-video push). */
export const CAMPAIGN_NAME = "gameplay-video";

/** One short link per channel. The key is the path (`/yt`). */
export const CAMPAIGN_LINKS = {
  yt: { source: "youtube", medium: "shorts" },
  tt: { source: "tiktok", medium: "video" },
  ig: { source: "instagram", medium: "reels" },
  x: { source: "x", medium: "social" },
} as const;

export type CampaignLinkKey = keyof typeof CAMPAIGN_LINKS;

export interface CampaignParams {
  campaign_source: string;
  campaign_medium?: string;
  campaign_name?: string;
}

let remembered: CampaignParams | null = null;
let urlChecked = false;

/** The URL a short link redirects to, e.g. `/play-free?utm_source=youtube&…`. */
export function campaignLandingUrl(key: CampaignLinkKey): string {
  const { source, medium } = CAMPAIGN_LINKS[key];
  const query = new URLSearchParams({
    utm_source: source,
    utm_medium: medium,
    utm_campaign: CAMPAIGN_NAME,
  });
  return `${CAMPAIGN_LANDING_PATH}?${query.toString()}`;
}

/** Remember the channel a short link was opened from, for the rest of this visit. */
export function rememberCampaignLink(key: CampaignLinkKey): void {
  const { source, medium } = CAMPAIGN_LINKS[key];
  remembered = { campaign_source: source, campaign_medium: medium, campaign_name: CAMPAIGN_NAME };
}

/** Read `utm_*` from a query string; null when it carries no `utm_source`. */
export function parseCampaignParams(search: string): CampaignParams | null {
  const query = new URLSearchParams(search);
  const source = query.get("utm_source");
  if (!source) return null;
  return {
    campaign_source: source,
    campaign_medium: query.get("utm_medium") ?? undefined,
    campaign_name: query.get("utm_campaign") ?? undefined,
  };
}

/**
 * The campaign this visit came from, or an empty object. A visitor who arrived on a full tagged
 * URL rather than a short link is picked up from the URL the first time this is asked.
 */
export function getCampaignParams(): Partial<CampaignParams> {
  if (!remembered && !urlChecked && typeof window !== "undefined") {
    urlChecked = true;
    remembered = parseCampaignParams(window.location.search);
  }
  return remembered ?? {};
}

/** Test seam: forget the remembered campaign. */
export function resetCampaignSourceForTests(): void {
  remembered = null;
  urlChecked = false;
}
